import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { MONTHS_IT } from "../lib/categories";
import { useSite } from "@/context/SiteData";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const AVAILABLE_MONTHS = [6, 7, 8, 9, 10, 11]; // 0-indexed: Jul..Dec 2026

export const CalendarView = ({ courses, onOpen }) => {
  const { catMap } = useSite();
  const [monthIdx, setMonthIdx] = React.useState(0); // index into AVAILABLE_MONTHS
  const month = AVAILABLE_MONTHS[monthIdx];
  const year = 2026;

  // map date -> courses (course appears on the FIRST giornata of each edition)
  const eventsByDate = React.useMemo(() => {
    const map = {};
    const push = (d, c) => {
      const arr = (map[d] = map[d] || []);
      if (!arr.some((x) => x.id === c.id)) arr.push(c);
    };
    courses.forEach((c) => {
      const eds = c.editions || [];
      if (eds.length) {
        eds.forEach((ed) => {
          const first = (ed.giornate || []).map((g) => g.date).filter(Boolean).sort()[0];
          if (first) push(first, c);
        });
      } else {
        (c.dates || []).forEach((d) => push(d, c));
      }
    });
    return map;
  }, [courses]);

  const firstDay = new Date(year, month, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div
      data-testid="calendar-view"
      className="bg-white rounded-3xl border border-gray-100 p-4 sm:p-8 shadow-[0_2px_10px_rgba(0,0,0,0.02)]"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display font-700 text-xl sm:text-2xl tracking-tight text-[#EA6A0A]">
          {MONTHS_IT[month]} {year}
        </h3>
        <div className="flex items-center gap-2">
          <button
            data-testid="calendar-prev"
            disabled={monthIdx === 0}
            onClick={() => setMonthIdx((i) => Math.max(0, i - 1))}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="calendar-next"
            disabled={monthIdx === AVAILABLE_MONTHS.length - 1}
            onClick={() => setMonthIdx((i) => Math.min(AVAILABLE_MONTHS.length - 1, i + 1))}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] sm:text-xs font-600 uppercase tracking-wide text-gray-400 py-2">
            {w}
          </div>
        ))}
      </div>

      <motion.div
        key={month}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-7 gap-1 sm:gap-2"
      >
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} className="aspect-square sm:min-h-[92px]" />;
          const events = eventsByDate[iso(d)] || [];
          const has = events.length > 0;
          return (
            <div
              key={d}
              data-testid={`calendar-day-${d}`}
              className={`aspect-square sm:aspect-auto sm:min-h-[92px] rounded-xl sm:rounded-2xl p-1.5 sm:p-2 border transition-colors ${has ? "border-gray-100 bg-white hover:border-[#EA6A0A]/20" : "border-transparent bg-gray-50/50"}`}
            >
              <span className={`text-xs sm:text-sm font-600 ${has ? "text-[#111827]" : "text-gray-300"}`}>
                {d}
              </span>
              <div className="mt-1 space-y-1 hidden sm:block">
                {events.slice(0, 2).map((c) => (
                  <button
                    key={c.id}
                    data-testid={`calendar-event-${c.id}-${d}`}
                    onClick={() => onOpen(c)}
                    className="w-full text-left truncate rounded-md px-1.5 py-1 text-[10px] font-600 transition-transform hover:scale-[1.02]"
                    style={{ background: `${(catMap[c.category]?.color || "#999")}14`, color: catMap[c.category]?.color || "#999" }}
                  >
                    {c.name}
                  </button>
                ))}
                {events.length > 2 && (
                  <span className="block text-[10px] text-gray-400 px-1.5">+{events.length - 2} altri</span>
                )}
              </div>
              {/* Mobile: dots */}
              <div className="flex sm:hidden flex-wrap gap-0.5 mt-1">
                {events.slice(0, 4).map((c) => (
                  <button
                    key={c.id}
                    data-testid={`calendar-dot-${c.id}-${d}`}
                    onClick={() => onOpen(c)}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: catMap[c.category]?.color || "#999" }}
                    aria-label={c.name}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </motion.div>

      <p className="mt-5 text-xs text-gray-400 text-center sm:hidden">
        Tocca i puntini colorati per aprire il dettaglio del corso.
      </p>
    </div>
  );
};
